<template>
  <v-container class="asset-overview-container">
    <v-row no-gutters>
      <v-col cols="12" md="1">
        <router-link :to="{ name: 'WorldWideAssets' }" class="back-arrow">
          <v-icon large color="mid-grey" class="mt-1">
            {{ icons.mdiArrowLeft }}
          </v-icon>
        </router-link>
      </v-col>
      <v-col cols="12" md="4" class="form-blurb">
        <h1 class="mb-2">Your assets</h1>
        <p>Tell us about your assets:</p>
        <ul class="mt-2 mb-4">
          <li>
            Here you need to add all assets that you want to explicitly donate
            to specific people and/or charities that won’t be catered for as
            part of the remainder of your estate.
          </li>
          <li>
            Listing all your assets will also ensure that your Executor is fully
            informed which will avoid unncessary delays in winding up your
            estate.
          </li>
        </ul>
      </v-col>
      <v-col cols="12" md="5" offset-md="1" class="mt-4">
        <h4 class="mb-3">
          <span v-if="testator.worldWideAssets" class="mb-3"
            >This will applies to your world wide assets</span
          >
          <span v-else class="mb-3"
            >This will only applies to your {{ willCountry }} assets</span
          >
        </h4>
        <h4 class="mb-4 assets-overview-info">
          You only need to list assets here which you specifically want to
          donate to someone. Any other assets you choose to list here will
          merely serve the purpose of helping your Executor when they need to
          wind up your estate.
        </h4>
        <div v-if="!loading">
          <v-col cols="12" class="relation-section">
            <h2>Properties</h2>
            <p>
              List all the properties you own, whether fully or partially,
              including those with or without a bond.
            </p>
            <div class="asset-list">
              <PropertiesList :testator="testator" />
            </div>
            <v-divider class="divider" />
          </v-col>

          <v-col cols="12" class="relation-section">
            <h2>Vehicles</h2>
            <p>List all your vehicles, whether they are financed or not.</p>
            <div class="asset-list">
              <VehiclesList :vehicles="testator.vehicles" />
            </div>
            <v-divider class="divider" />
          </v-col>

          <v-col cols="12" class="relation-section">
            <h2>Financial Assets</h2>
            <p>
              Include financial assets you hold, whether through investment
              companies or in private companies.
            </p>
            <div class="asset-list">
              <FinancialAssetsList
                :financial-assets="testator.financialAssets"
              />
            </div>
            <v-divider class="divider" />
          </v-col>

          <v-col cols="12" class="relation-section">
            <h2>Other significant assets</h2>
            <p>
              Include, for instance, jewelry, art, valuable collector's pieces.
            </p>
            <div class="asset-list">
              <OtherAssetsList :other-assets="testator.otherAssets" />
            </div>
            <v-divider class="divider" />
          </v-col>

          <v-col cols="12">
            <Checkbox
              v-if="!hasAssets"
              v-model="assetsOptOut"
              :label="`I don't want to list any assets`"
              color="primary"
            />
            <GradientButton
              @gradientButtonClicked="saveAndContinue"
              :title="'Save And Continue'"
              :small="true"
              :loading="buttonLoading"
              :callout="'right'"
              :disabled="!hasAssets && !assetsOptOut"
            />
          </v-col>
        </div>

        <div v-if="loading" class="text-center">
          <v-progress-circular indeterminate color="primary" />
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
  import PropertiesList from './PropertiesList.vue';
  import VehiclesList from './VehiclesList.vue';
  import FinancialAssetsList from './FinancialAssetsList.vue';
  import OtherAssetsList from './OtherAssetsList.vue';
  import {
    UPDATE_TESTATOR_MUTATION,
    TESTATOR_QUERY,
  } from '../../constants/graphql/testators';
  import { formHelperMixin } from '../../mixins/form-helper-mixin';

  export default {
    name: 'AssetsOverview',
    components: {
      PropertiesList,
      VehiclesList,
      FinancialAssetsList,
      OtherAssetsList,
    },
    mixins: [formHelperMixin],
    data() {
      return {
        testator: {},
        loading: true,
        assetsOptOut: false,
      };
    },
    computed: {
      hasAssets() {
        return (
          this.testator.properties.length > 0 ||
          this.testator.vehicles.length > 0 ||
          this.testator.otherAssets.length > 0 ||
          this.testator.financialAssets.length > 0
        );
      },
      willCountry() {
        return this.testator.willCountry == 'ZA'
          ? 'South African'
          : 'United Kindgom';
      },
    },
    watch: {
      'testator.assetsOptOut': {
        immediate: true,
        handler(newVal) {
          this.assetsOptOut = Boolean(newVal);
        },
      },
    },
    methods: {
      saveAndContinue() {
        if (this.hasAssets) {
          this.redirectToNext();
        } else {
          this.updateTestator().then(_result => {
            this.redirectToNext();
          });
        }
      },
      updateTestator() {
        this.buttonLoading = true;
        const testatorInputDetails = {
          assetsOptOut: this.assetsOptOut,
        };
        const id = this.testatorId;

        return this.$apollo
          .mutate({
            mutation: UPDATE_TESTATOR_MUTATION,
            variables: {
              id,
              testatorInputDetails,
            },
          })
          .catch(error => {
            this.displayError();
            throw error;
          });
      },
      redirectToNext() {
        this.$router.push({ name: 'OverviewPage', hash: '#your-assets' });
      },
    },
    apollo: {
      testator: {
        query: TESTATOR_QUERY,
        variables() {
          return {
            id: this.testatorId,
          };
        },
        result(response) {
          if (response.data) {
            this.assetsOptOut = Boolean(response.data.testator.assetsOptOut);
            this.loading = false;
          }
        },
      },
    },
  };
</script>

<style scoped lang="scss">
  .asset-overview-container {
    text-align: left;
    margin-bottom: 60px;

    .relation-section {
      margin-bottom: 35px;

      .asset-list {
        margin: 10px 0;
      }

      .divider {
        margin-top: 15px;
      }
    }

    .assets-overview-info {
      font-size: 16px;
      font-weight: 400;
      line-height: 24px;
      text-align: left;
    }
  }
</style>
